import { RuleGroupType, RuleType } from 'react-querybuilder';

/**
 * Converts a MongoDB query to react-querybuilder RuleGroupType format
 * @param mongoQuery MongoDB query object
 * @returns RuleGroupType for react-querybuilder
 */
export function mongoQueryToRuleGroup(mongoQuery: Record<string, any>): RuleGroupType {
  if (!mongoQuery || Object.keys(mongoQuery).length === 0) {
    return {
      combinator: 'and',
      rules: []
    };
  }

  // Handle top-level logical operators
  if (mongoQuery.$and) {
    return {
      combinator: 'and',
      rules: mongoQuery.$and.map((subQuery: any) => processQuery(subQuery))
    };
  }

  if (mongoQuery.$or) {
    return {
      combinator: 'or',
      rules: mongoQuery.$or.map((subQuery: any) => processQuery(subQuery))
    };
  }

  // Handle implicit AND at top level
  const rules: (RuleType | RuleGroupType)[] = [];
  for (const [field, condition] of Object.entries(mongoQuery)) {
    if (!field.startsWith('$')) {
      rules.push(processFieldCondition(field, condition));
    }
  }

  return {
    combinator: 'and',
    rules
  };
}

function processQuery(query: any): RuleType | RuleGroupType {
  if (query.$and) {
    return {
      combinator: 'and',
      rules: query.$and.map((subQuery: any) => processQuery(subQuery))
    };
  }

  if (query.$or) {
    return {
      combinator: 'or',
      rules: query.$or.map((subQuery: any) => processQuery(subQuery))
    };
  }

  // Process single field condition
  const fields = Object.keys(query).filter(key => !key.startsWith('$'));
  if (fields.length === 1) {
    return processFieldCondition(fields[0], query[fields[0]]);
  }

  // Multiple fields - implicit AND
  return {
    combinator: 'and',
    rules: fields.map(field => processFieldCondition(field, query[field]))
  };
}

function processFieldCondition(field: string, condition: any): RuleType {
  // Simple equality
  if (typeof condition !== 'object' || condition === null) {
    return {
      field,
      operator: 'eq',
      value: condition
    };
  }

  // Handle operators
  const operators = Object.keys(condition);
  if (operators.length === 1) {
    const operator = operators[0];
    const value = condition[operator];

    switch (operator) {
      case '$eq':
        return { field, operator: 'eq', value };
      case '$ne':
        return { field, operator: 'ne', value };
      case '$gt':
        return { field, operator: 'gt', value };
      case '$gte':
        return { field, operator: 'gte', value };
      case '$lt':
        return { field, operator: 'lt', value };
      case '$lte':
        return { field, operator: 'lte', value };
      case '$in':
        return {
          field,
          operator: 'in',
          value: Array.isArray(value) ? value.join(', ') : value
        };
      case '$nin':
        return {
          field,
          operator: 'nin',
          value: Array.isArray(value) ? value.join(', ') : value
        };
      case '$regex':
        return { field, operator: 'regex', value };
      case '$exists':
        return { field, operator: 'exists', value: value ? 'true' : 'false' };
      default:
        // Default to equality for unknown operators
        return { field, operator: 'eq', value: condition };
    }
  }

  // Multiple operators on same field - not directly supported, default to first
  return { field, operator: 'eq', value: condition };
}
